import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'

// Use
import useMounted from 'use/mounted'

// Components
import Progress from 'antd/lib/progress'
import Loading from 'ui/Loading'
import IconRemove from 'ui/Icons/IconRemove'
import IconDownload from 'ui/Icons/IconDownload'

// Styles
import './FileItem.scss'

export const STATUS_UPLOADING = 'uploading'
export const STATUS_DONE = 'done'
export const STATUS_ERROR = 'error'

const FileItem = ({
  name,
  error,
  percent,
  status,
  onDownload,
  onRemove,
  onClick,
}) => {
  const isMounted = useMounted()
  const [loading, setLoadingState] = useState(false)
  const isUploading = useMemo(() => status === STATUS_UPLOADING, [status])
  const isError = useMemo(() => status === STATUS_ERROR, [status])
  const isRemovable = useMemo(
    () => status !== STATUS_UPLOADING && !!onRemove,
    [onRemove, status]
  )
  const isDownloadable = useMemo(
    () => status === STATUS_DONE && !!onDownload,
    [onDownload, status]
  )
  const displayName = name ? name.split('/').pop() : name

  // Class list
  const classList = useMemo(() => {
    const classes = ['x-file-item']

    if (isUploading) {
      classes.push('has-progress')
    }

    if (isError) {
      classes.push('has-error')
    }

    if (loading) {
      classes.push('has-loading')
    }

    return classes.join(' ')
  }, [isUploading, isError, loading])

  const setLoading = useCallback(
    (value) => {
      if (isMounted.current) {
        setLoadingState(value)
      }
    },
    [isMounted]
  )

  return (
    <div
      className={classList}
      style={{
        cursor: onClick ? 'pointer' : 'inherit',
      }}
      onClick={() => {
        if (onClick) {
          onClick(name, setLoading)
        }
      }}
    >
      <div className="left">
        {loading && <Loading spinning={loading} size={20} />}
      </div>
      <div className="center">
        <div className="top">
          <div title={displayName} className="name">
            {displayName}
          </div>
        </div>

        {isUploading && (
          <Progress
            status="active"
            showInfo={false}
            percent={percent}
            size="small"
          />
        )}

        {error && <div className="error">{error}</div>}
      </div>
      <div className="right">
        {isDownloadable && (
          <IconDownload
            className="file-item-download"
            width="20px"
            height="20px"
            clickable
            onClick={() => onDownload(name, setLoading)}
          />
        )}
        {isRemovable && (
          <IconRemove
            className="file-item-remove"
            width="13px"
            height="13px"
            color={isError ? '#FA6383' : '#5BADFA'}
            clickable
            onClick={() => onRemove(name, setLoading)}
          />
        )}
      </div>
    </div>
  )
}

FileItem.propTypes = {
  name: PropTypes.string,
  error: PropTypes.any,
  status: PropTypes.string,
  percent: PropTypes.number,
  onRemove: PropTypes.func,
  onDownload: PropTypes.func,
  onClick: PropTypes.func,
}

FileItem.defaultProps = {
  name: '',
  error: '',
  percent: 0,
  status: '',
  onClick: null,
  onRemove: null,
  onDownload: null,
}

export default FileItem
